/**
 * Where a comment sits within a run: consecutive comments by one author,
 * which the list shows as a single card.
 *
 * The first comment of a run carries the avatar and name; the rest show their
 * body beneath a hairline that carries only their time, the way chat clients
 * coalesce a burst of messages from one person. Port of `FeedbackRuns` in
 * `apps/ios/bandwith/Views/Feedback/FeedbackRuns.swift`; the two must agree
 * or the same list groups differently on phone and laptop.
 */
export type FeedbackCardSegment =
  /** A comment with no run-mates: a complete card of its own. */
  | "only"
  /** The top of a run: author line, rounded top corners, open bottom. */
  | "first"
  /** Inside a run: hairline instead of an author line, square corners. */
  | "middle"
  /** The bottom of a run: hairline, rounded bottom corners. */
  | "last";

/** Whether this comment opens a card, and so shows the author line. */
export function opensCard(segment: FeedbackCardSegment): boolean {
  return segment === "only" || segment === "first";
}

/** Whether this comment closes a card. */
export function closesCard(segment: FeedbackCardSegment): boolean {
  return segment === "only" || segment === "last";
}

/**
 * The segment for each key, in the order given. Adjacent items join a run
 * when their keys are equal and non-null.
 *
 * The key is the author id, except that the web passes null for the
 * amplified comment: it has its own card styling, and one amber segment in
 * the middle of a plain card would read as broken. iOS has no amplify, so
 * its `segments(for:)` compares author ids directly.
 */
export function runSegments(keys: (string | null)[]): FeedbackCardSegment[] {
  // joins(i) is true when keys[i] and keys[i + 1] share a card.
  const joins = (index: number) => {
    const key = keys[index];
    return key != null && key === keys[index + 1];
  };
  return keys.map((_, index) => {
    const joinedAbove = index > 0 && joins(index - 1);
    const joinedBelow = joins(index);
    if (joinedAbove) return joinedBelow ? "middle" : "last";
    return joinedBelow ? "first" : "only";
  });
}

/**
 * Whether each row prints its time, given each row's time label and segment.
 * The row that opens a card always does. A hairline leaves its time off when
 * it would read the same as the row above ("1 week ago" under "1 week ago"),
 * so a burst of comments from one sitting shows the time once, at the top of
 * the burst, and again only where it changes.
 */
export function printsTime(
  labels: string[],
  segments: FeedbackCardSegment[],
): boolean[] {
  if (labels.length !== segments.length) {
    throw new Error("printsTime: one label per segment");
  }
  return segments.map(
    (segment, index) =>
      opensCard(segment) || index === 0 || labels[index] !== labels[index - 1],
  );
}
